import { chartRecordRemovalReasonLabel } from '@/components/ui/chart-record-removal-reasons'
import dayjs from 'dayjs'
import type {
  AuditAccessEvent,
  AuditAccessResult,
  AuditAccessSource,
  AuditAccessType,
  AuditChangeEvent,
  AuditChangeSource,
} from '@behuman/core-contract'
import { SECTION_RESOURCE_TYPES } from '@behuman/core-contract'

/**
 * PD-1322 — display vocabulary + client-side filtering for the Chart Audit
 * section. The five mockups (Jira attachments 23671-23675) are the spec.
 */

export const ACCESS_SOURCE_LABELS: Record<AuditAccessSource, string> = {
  'behuman-web': 'BeHuman Web',
  'ai-service': 'AI Service',
  api: 'API',
  // S21 — the ticket names the integrations individually ("TEFCA, Health Gorilla, eRx
  // or other integration") rather than as one bucket.
  tefca: 'TEFCA',
  'health-gorilla': 'Health Gorilla',
  erx: 'eRx',
  'other-integration': 'Other Integration',
  // S28 — captured by ingesting Medplum's own AuditEvent stream rather than reported by a
  // client. A capture mechanism, not a channel: the log cannot tell a practitioner token used
  // from the web app apart from the same token used against the API, so this label says what
  // the record actually establishes. See AUDIT_ACCESS_SOURCE_VALUES.
  'medplum-audit': 'FHIR Audit Log',
  // Superseded by the four above and written by nothing; present because the value is
  // retained in the contract so migration 0011 can apply. See AUDIT_ACCESS_SOURCE_VALUES.
  integration: 'Integration',
}

export const CHANGE_SOURCE_LABELS: Record<AuditChangeSource, string> = {
  manual: 'Manual',
  ai: 'AI Generated',
  integration: 'Integration',
}

export const RESULT_LABELS: Record<AuditAccessResult, string> = {
  success: 'Successful',
  failure: 'Failed',
  denied: 'Denied',
}

/** Mockup section labels for the change-history rows, by FHIR resource type. */
const RESOURCE_SECTION_LABELS: Record<string, string> = {
  /**
   * QA round 2 — 'Document', not 'Progress Note'.
   *
   * A DocumentReference is any document: a progress note, a scribe transcript, a generated
   * clinical summary, a consent PDF, the ID card image uploaded on the Demographics tab.
   * Labelling the type as one of them made every document's change read as a progress
   * note's, which is how replacing a patient ID produced "Updated Progress Note Status".
   *
   * The FALLBACK only. A row whose derivation recorded the document's own type renders that
   * instead — see {@link changeRecordLabel}. This is what rows derived before the label
   * existed, and any document with no type at all, read as.
   */
  DocumentReference: 'Document',
  MedicationStatement: 'Medication',
  Condition: 'Diagnosis',
  AllergyIntolerance: 'Allergy',
  Observation: 'Vitals',
  Task: 'Task',
  Patient: 'Demographics',
  Coverage: 'Insurance',
  ServiceRequest: 'Order',
  DiagnosticReport: 'Result',
  Consent: 'Consent',
  Appointment: 'Appointment',
  // S19 — the billing domain. Claim carries the codes, modifiers and linked diagnoses;
  // claim status is a tag on the Encounter.
  Encounter: 'Encounter',
  Claim: 'Billing',
  // QA round 2 — the chart sections whose resource types the change mapping had never
  // covered. Without a label each row would render the raw FHIR type name.
  Communication: 'Care Coordination Note',
  Procedure: 'Procedure',
  Immunization: 'Immunization',
  FamilyMemberHistory: 'Family History',
  RequestGroup: 'Order Group',
}

export function sectionLabelForResourceType(resourceType: string): string {
  return RESOURCE_SECTION_LABELS[resourceType] ?? resourceType
}

/**
 * QA round 3 (independent re-test) — Access History rows and the section filter showed
 * raw dataKey slugs ('labs-imaging', 'history-physical') where Change History showed
 * names. The irregular slugs are spelled out; everything else title-cases its
 * hyphen-split words, so a NEW section reads acceptably without an entry here.
 */
const SECTION_SLUG_LABELS: Record<string, string> = {
  'labs-imaging': 'Labs/Imaging',
  'history-physical': 'History and Physical',
  audit: 'Audit',
}

export function sectionSlugLabel(slug: string): string {
  const known = SECTION_SLUG_LABELS[slug]
  if (known) return known
  return slug
    .split('-')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ')
}

/**
 * The chart sections a change to this resource type belongs to.
 *
 * Greptile on !1354 — the filter vocabularies were accumulated from ACCESS events only, so
 * a section that has changes but no recorded views (any of the five newly derived ones, for
 * a chart nobody has browsed section by section) could not be selected on the Change History
 * tab, and neither could an actor who edited the chart without opening it. Change events
 * carry no `sections` array of their own, so the section is recovered the same way the
 * server recovers it: through the shared section-to-resource-type map.
 *
 * A DocumentReference legitimately answers to several sections (progress notes, documents,
 * consents), so it contributes all of them. That matches what the server's section filter
 * actually returns for each.
 */
export function sectionsForResourceType(resourceType: string): string[] {
  return Object.entries(SECTION_RESOURCE_TYPES)
    .filter(([, types]) => (types as readonly string[]).includes(resourceType))
    .map(([section]) => section)
}

/**
 * What a change row calls the record it is about: the record's own label when the
 * derivation recorded one, otherwise the resource type's.
 *
 * Only DocumentReference carries a label today, because it is the one tracked type that is
 * many different things — a progress note, a consent, a scribe transcript, the ID card
 * uploaded on Demographics. Rows derived before the label existed have none and keep
 * rendering exactly as they always did.
 */
export function changeRecordLabel(event: AuditChangeEvent): string {
  return event.resourceLabel?.trim() || sectionLabelForResourceType(event.resourceType)
}

/**
 * WHICH record, where {@link changeRecordLabel} says what KIND — "Insurance card (Aetna
 * front)" rather than a third row reading "Insurance card".
 *
 * QA round 3 (re-test) found every document of one type rendering identically: three cards
 * on a chart produced three "Added insurance card" rows with nothing to tell them apart,
 * on the tab whose job is saying which record changed. The name comes from the document's
 * own description, the same field the Documents viewer titles it with.
 *
 * The TYPE is kept alongside rather than replaced. It is what makes a row scannable — a
 * reader looking for consent changes finds them by type — and a description alone
 * ("Scan 3") often says nothing about what the record IS. Rows with no name, which is
 * everything derived before the field existed, render exactly as they did before.
 *
 * QA round 3 (re-test) — THE TYPE AND THE NAME OVERLAP, and equality was too narrow a test
 * for it. Three doublings on one screen: "Risks & Gaps Report (Risks & Gaps Report
 * (pending))", "Clinical Summary Overview (Clinical Summary Overview (pending))" and
 * "Progress Note (Virtual Visit) (Virtual Visit Summary)". Both halves are free text a
 * writer chose — `type.text` and `description` — so they are related by containment far
 * more often than by equality, and the three cases below are the shapes that produces.
 */
export function changeRecordTitle(event: AuditChangeEvent): string {
  const label = changeRecordLabel(event)
  const name = event.resourceName?.trim()
  if (!name) return label
  // The same words in both halves: the TYPE's casing wins. It is the one of the two the
  // product chose rather than a writer typed, so "History and Physical" is preferred over
  // a `description` reading "history and physical". Checked before containment, which is
  // true in both directions here and would otherwise hand the row the lowercased copy.
  if (name.toLowerCase() === label.toLowerCase()) return label
  // The name already spells the type out and then some — "Risks & Gaps Report (pending)"
  // against the type "Risks & Gaps Report". The name is the longer, more specific
  // statement, so it stands alone; appending it to the type said the type twice.
  if (contains(name, label)) return name
  // The reverse: the type already spells the name out, so the name adds nothing.
  if (contains(label, name)) return label
  // A type QUALIFIED with something the name repeats — `type.text` "Progress Note (Virtual
  // Visit)" against the description "Virtual Visit Summary". Neither contains the other,
  // so the two rules above leave "Progress Note (Virtual Visit) (Virtual Visit Summary)":
  // "Virtual Visit" twice, in a doubled parenthetical. The qualifier is what the name is
  // already saying, so the name replaces it and the bare type is kept.
  const qualified = label.match(/^(.+?)\s*\(([^()]+)\)$/)
  if (qualified && contains(name, qualified[2])) return `${qualified[1]} (${name})`
  return `${label} (${name})`
}

/**
 * Case-insensitive containment, the one comparison {@link changeRecordTitle} makes. Both
 * sides are free text a writer typed, so case is not meaningful here — but only for
 * DECIDING; whichever string is rendered is rendered exactly as it was written, because an
 * audit trail that re-cases a name is editing the record.
 */
function contains(outer: string, inner: string): boolean {
  return outer.toLowerCase().includes(inner.toLowerCase())
}

/**
 * Reader-facing names for change fields whose FHIR path says nothing to a compliance
 * reader. `meta.tag` is where claim status lives (see META_TAG_FIELD in the middleware's
 * change-history.service.ts), so "meta.tag" would hide exactly the change the ticket asks
 * to see.
 */
const CHANGE_FIELD_LABELS: Record<string, string> = {
  'meta.tag': 'status tags',
  resource: 'record',
  // Internal build-status extensions are now filtered out server-side (see
  // INTERNAL_EXTENSION_URLS in the middleware's change-history.service.ts), so a surviving
  // `extension` row is a real one and the plural reads better than the FHIR element name.
  extension: 'extensions',
}

export function changeFieldLabel(field: string): string {
  return CHANGE_FIELD_LABELS[field] ?? field
}

/**
 * Who made a change, as one label — name plus role.
 *
 * The role is not decoration: attribution now falls back to the FHIR version's
 * `meta.author`, which on most writes is a `ClientApplication` (our own middleware). Showing
 * "Middleware" alone would read like a person who touched the chart, so the role rides along
 * and says 'System Service'. Used by the rows, the detail panel AND both exports so a reader
 * cannot be told three different things about one event.
 */
export function changeActorLabel(event: AuditChangeEvent): string {
  const name = event.actorName ?? 'Unknown'
  return event.actorRole ? `${name} · ${event.actorRole}` : name
}

/**
 * Lowercases the TYPE at the head of a record title and nothing else.
 *
 * The type is the half the product chose, so casing it to suit the sentence is ours to
 * do; the name is what a person typed, and an audit trail that re-cases a name is editing
 * the record. Splitting on the first " (" got that wrong whenever the NAME ALREADY
 * CONTAINS ITS TYPE — `changeRecordTitle` then returns the name alone ("Order Paperwork
 * Scan QA3" for a type of "Order"), there is no parenthesis to split on, and the whole
 * string was lowercased: the CSV read "Added order paperwork scan qa3" against a screen
 * saying "Order Paperwork Scan QA3".
 *
 * So the type is matched rather than guessed at. Nothing is lowercased unless the title
 * demonstrably opens with it — bare (the title IS the type) or qualified (the type, then
 * the name in parentheses). A title that opens with anything else is a name, and is left
 * exactly as written.
 */
function lowercaseRecordType(title: string, type: string): string {
  if (title.toLowerCase() === type.toLowerCase()) return title.toLowerCase()
  // `changeRecordTitle`'s qualified branch keeps the type WITHOUT its own parenthetical
  // ("Progress Note (Virtual Visit)" + name "Virtual Visit Summary" → "Progress Note
  // (Virtual Visit Summary)"), so the bare type is what the title actually opens with.
  const bare = type.replace(/\s*\([^()]*\)$/, '')
  if (title.toLowerCase().startsWith(`${bare.toLowerCase()} (`)) {
    return title.slice(0, bare.length).toLowerCase() + title.slice(bare.length)
  }
  return title
}

export function changeEventTitle(event: AuditChangeEvent, record?: string): string {
  // The TYPE is lowercased so the sentence reads as English ("Added medication", not
  // "Added Medication"). The record's own NAME is not: "Aetna front" is what someone
  // typed, and an audit trail that quietly re-cases a name is editing the record. The
  // boundary is ours — changeRecordTitle is what appends " (name)".
  const type = changeRecordLabel(event)
  const label = lowercaseRecordType(record ?? type, type)
  // QA round 2 — both whole-record acts read as the act, with no field name after them:
  // `field` is 'resource' for each, and "Added medication resource" is not English.
  if (event.action === 'created') return `Added ${label}`
  if (event.action === 'deleted') return `Deleted ${label}`
  // QA round 3 (re-test) — the soft delete behind "Remove from chart", the only removal a
  // clinician can perform. "from chart" is not decoration: it is what distinguishes this
  // from the hard delete above, which takes the record out of FHIR entirely. Also a
  // whole-record act, so no field name follows it — the row's field is 'status', and
  // "Removed id card from chart status" is not English.
  if (event.action === 'removed') return `Removed ${label} from chart`
  // Note lifecycle (S15): signed / addendum / amended render distinctly.
  if (event.action === 'signed') return `Signed ${label}`
  if (event.action === 'addendum') return `Addendum to ${label}`
  if (event.action === 'amended') return `Amended ${label} ${changeFieldLabel(event.field)}`
  return `Updated ${label} ${changeFieldLabel(event.field)}`
}

/**
 * A removal's reason in the words the clinician was offered, not the slug that was stored.
 *
 * The middleware puts the reason on removal rows as `<slug>` or `<slug> — <explanation>`
 * (change-history.service.ts), because the picker's wording is web copy and does not
 * belong in the derivation. Every other action's `reason` is already free text from a
 * Provenance and passes through untouched.
 */
function removalReasonText(event: AuditChangeEvent): string | undefined {
  if (event.action !== 'removed' || !event.reason) return event.reason
  const [slug, ...rest] = event.reason.split(' — ')
  const label = chartRecordRemovalReasonLabel(slug)
  return rest.length > 0 ? `${label} — ${rest.join(' — ')}` : label
}

/**
 * S20 — the ticket's per-change metadata, as label/value pairs, skipping everything the
 * record does not carry. Absent is rendered as absent rather than as an em dash, because
 * "the FHIR record does not say" is a different statement from "there was none".
 */
export function changeMetadataEntries(event: AuditChangeEvent): Array<[string, string]> {
  // QA round 3 (re-test) — the related encounter must not echo the record itself. FHIR R4
  // gives Encounter and Appointment no encounter link of their own, so the derivation
  // records THEM as their own context (encounterReferenceOf in the middleware's
  // change-history.service.ts); an appointment row therefore showed "RECORD
  // Appointment/b2771db4-…" and, directly beside it, "RELATED ENCOUNTER
  // Appointment/b2771db4-…" — the same value, under a label that says it is a different
  // resource of a different type. Dropped here rather than at the derivation so the rows
  // already stored stop repeating themselves too.
  const record = `${event.resourceType}/${event.resourceId}`
  return (
    [
      // QA round 3 — the ticket's "immutable user ID": the actor's stable FHIR
      // reference, shown alongside the display name because names change and collide.
      ['Actor ID', event.actorReference],
      ['Reason', removalReasonText(event)],
      ['Related encounter', event.relatedEncounter === record ? undefined : event.relatedEncounter],
      ['Reviewer or approver', event.reviewer],
      ['Application version', event.applicationVersion],
      ['Correlation ID', event.correlationId],
    ] satisfies Array<[string, string | undefined]>
  ).filter((entry): entry is [string, string] => Boolean(entry[1]))
}

// ---------------------------------------------------------------------------
// Filters — the six mockup dimensions (attachment 23675): date range, user role,
// event result, source, emergency-access only, downloads only — plus clinic, the
// seventh, which AC8 requires ("date, user, role, action, section, clinic, source
// and result") and the mockups predate.
// ---------------------------------------------------------------------------

export const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
]

/**
 * QA round 1 (W8) — the roles the system actually records, replacing the mockup list
 * (attachment 23674) whose "Billing Specialist" exists nowhere in this codebase and
 * could only ever return an empty list.
 *
 * The filter is a plain string equality on the stored `actorRole`, so every entry here
 * must be a value the middleware writes: the four PractitionerRole-derived labels plus
 * 'Super Admin' (PRACTITIONER_ROLE_LABELS / resolveActorRole in the middleware's
 * audit-actor-context.ts), and 'System Service', which the change derivation records
 * for machine authors (SYSTEM_ACTOR_ROLE in change-history.service.ts). Rows whose role
 * fell back to a raw resourceType ('Practitioner', 'Patient') are reachable via 'All
 * roles'.
 */
/**
 * QA round 3 (re-test) — the role options are the roles the RECORD ACTUALLY HOLDS, not a
 * list of the ones we expected to write.
 *
 * A hardcoded list cannot be complete here, and measurably was not: both audit tables are
 * append-only, so every row keeps whatever role it was written with, forever. On a real
 * dev chart 855 access rows carried `Practitioner` and 15 carried `Patient` — the
 * resourceType fallback, written before roles resolved to app-level labels — and change
 * rows additionally carried `Recorder`, `Verifier` and `Performer` from Provenance agent
 * types. None of those were selectable, so **two thirds of the trail was unreachable by
 * any role filter**, which is the same defect QA filed against the Change History tab in a
 * narrower form.
 *
 * The server now returns the distinct roles observed across both tables
 * (`auditListFilterOptions`), exactly as it already did for users, sections and clinics.
 * The filter is a plain string equality on the stored value, so options derived from the
 * stored values can never offer a dead choice or hide a live one.
 */
export function buildRoleOptions(roles: string[]): Array<{ value: string; label: string }> {
  return [
    { value: 'all', label: 'All roles' },
    ...[...roles].sort((a, b) => a.localeCompare(b)).map((role) => ({ value: role, label: role })),
  ]
}

export const EVENT_RESULT_OPTIONS = [
  { value: 'all', label: 'All results' },
  { value: 'success', label: 'Successful' },
  { value: 'failure', label: 'Failed' },
  { value: 'denied', label: 'Denied' },
]

/**
 * The mockup's Source filter, widened to the ticket's source vocabulary (S21).
 *
 * `integration` is deliberately absent: it is retained in the contract only so migration
 * 0011's CHECK can apply to rows written under 0010, and no code writes it, so offering
 * it would be a filter that can only ever return nothing.
 */
export const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: 'behuman-web', label: 'BeHuman Web' },
  { value: 'ai-service', label: 'AI Service' },
  { value: 'api', label: 'API' },
  { value: 'tefca', label: 'TEFCA' },
  { value: 'health-gorilla', label: 'Health Gorilla' },
  { value: 'erx', label: 'eRx' },
  { value: 'other-integration', label: 'Other Integration' },
  // S28 — offered, unlike `integration`, because rows carrying it are written as soon as the
  // CloudWatch ingest is configured for the environment.
  { value: 'medplum-audit', label: 'FHIR Audit Log' },
  /**
   * QA round 2 — the one CHANGE source with no access-side spelling, and so the one value
   * a reader could never select: `changeSourceForAccessSource` reached 'ai' and
   * 'integration' through their access equivalents, leaving 'manual' unreachable and the
   * Manual badge unfilterable.
   *
   * Last in the list because it answers a different question from the entries above it —
   * those name a channel a request arrived through, this names how a change was authored.
   * On the Access History tab it imposes no filter, for the same reason 'behuman-web'
   * imposes none on changes: an access has no manual/AI distinction to narrow by.
   */
  { value: 'manual', label: 'Manual' },
]

/**
 * The clinic filter's options, from the organizations the events actually named (AC8's
 * clinic dimension) — a reference→label map, accumulated across responses by ChartAudit
 * (QA W5: the vocabulary only grows, so a server-narrowed response cannot remove an
 * option the reader could pick a moment ago).
 *
 * Derived rather than fetched. Nothing in `web` lists organizations — `useOrganization` reads
 * one by id — so a hardcoded or fetched list would have to be reconciled against what the
 * audit rows carry, and would offer clinics that can only ever return nothing. Each event
 * carries both halves, so the option's VALUE is the reference the server matches on
 * and its LABEL is the name the reader recognises.
 *
 * Events with no organization contribute nothing: an unresolved clinic is "the record does
 * not say", which is not a value anyone can usefully filter to.
 */
export function buildClinicOptions(
  clinicsByReference: ReadonlyMap<string, string>,
): Array<{ value: string; label: string }> {
  return [
    { value: 'all', label: 'All clinics' },
    ...[...clinicsByReference].map(([value, label]) => ({ value, label })),
  ]
}

/**
 * Access sources that mean "an external integration wrote this". Change events carry the
 * coarser manual/ai/integration vocabulary, so any of these narrows change rows to
 * `integration` — see applyChangeFilters.
 */
const INTEGRATION_ACCESS_SOURCES = new Set<string>([
  'tefca',
  'health-gorilla',
  'erx',
  'other-integration',
  'integration',
])

export type AuditFilters = {
  /** Days back, or 'all'. */
  dateRange: string
  userRole: string
  eventResult: string
  source: string
  /**
   * AC8's clinic dimension: 'all', or an `Organization/<id>` reference. The REFERENCE and
   * not the display name, because that is what the server matches on — see
   * actorOrganizationReference in the middleware's audit.contract.ts. The drawer shows
   * the name and keeps the reference as the option's value.
   */
  clinic: string
  /**
   * Always false: the drawer's toggle was removed for this release because NOTHING writes
   * an emergency access event. Every capture path hardcodes `accessType: 'standard'` and
   * there is no break-glass flow, so the control could only ever return an empty table.
   * The plumbing below is intact so restoring the toggle is a one-line change once a
   * break-glass flow exists.
   */
  emergencyOnly: boolean
  downloadsOnly: boolean
  /** Inline search box (team member or activity). */
  search: string
  /** Inline "All users" select value ('all' or an actor name). */
  user: string
  /** Inline "All sections" select value ('all' or a section dataKey). */
  section: string
}

/**
 * QA round 3 — what the header's Last accessed / Last modified cards must SAY about
 * their own scope. The cards derive from the current (server-filtered) view, so with
 * any filter active they are facts about the view, not the chart — and even the
 * default view is a 30-day window. The scope note makes the claim match the data:
 * undefined only when the view genuinely spans the whole record.
 */
export function auditScopeNote(filters: AuditFilters): string | undefined {
  const narrowedBeyondDate =
    filters.userRole !== 'all' ||
    filters.eventResult !== 'all' ||
    filters.source !== 'all' ||
    filters.clinic !== 'all' ||
    filters.emergencyOnly ||
    filters.downloadsOnly ||
    filters.search.trim() !== '' ||
    filters.user !== 'all' ||
    filters.section !== 'all'
  // Paging back through older events is a POSITION in the record, not a narrowing of it,
  // so it does not make the view "filtered" — an earlier version conflated the two and
  // labelled a reader who simply pressed "Show older events" as having applied filters.
  // The window still bounds what the card can see, which the date-range note below
  // already tells the reader.
  if (narrowedBeyondDate) return 'filtered view'
  if (filters.dateRange !== 'all') {
    return optionLabel(DATE_RANGE_OPTIONS, filters.dateRange, '').toLowerCase() || undefined
  }
  return undefined
}

export const DEFAULT_AUDIT_FILTERS: AuditFilters = {
  dateRange: '30',
  userRole: 'all',
  eventResult: 'all',
  source: 'all',
  clinic: 'all',
  emergencyOnly: false,
  downloadsOnly: false,
  search: '',
  user: 'all',
  section: 'all',
}

/**
 * The inclusive lower bound "last N days" means — ONE value, used by the two query
 * pushdowns and by the client-side predicate alike.
 *
 * Anchored to the START of the day N days back, not to `now`. The bound is part of both
 * query inputs, and `now` produces a different millisecond on every render, so an
 * unanchored value gave each keystroke in the search box a fresh query key: a refetch per
 * character, against endpoints that run the change-history sync inline. Day granularity is
 * stable for a session-day, and it only ever WIDENS the window — for a compliance list
 * that is the safe direction, because it can add an event but never hide one.
 *
 * Inclusive, matching the `from` filter the endpoints document; the predicate below uses
 * the same boundary so the client half of a filter cannot drop a row the server half said
 * matched.
 */
export function auditRangeStart(dateRange: string): string | undefined {
  if (dateRange === 'all') return undefined
  return dayjs().subtract(Number(dateRange), 'day').startOf('day').toISOString()
}

function withinRange(recordedAt: string, dateRange: string): boolean {
  const start = auditRangeStart(dateRange)
  return start === undefined || !dayjs(recordedAt).isBefore(start)
}

/**
 * The filters the SERVER can apply, as query input.
 *
 * These used to run client-side over whatever the access-events endpoint happened to
 * return. That endpoint caps its page, so "last 90 days" was answered from the newest
 * page only and the CSV export — which exports the filtered table — inherited the gap.
 * Pushing them down means the cap applies to the matching set, and the response says
 * when it was hit.
 *
 * `search` stays client-side on purpose: it refines what is already on screen and has no
 * server-side counterpart. The client-side predicates below are kept as well, so the two
 * cannot disagree about what a filter means; with pushdown in place they are a no-op.
 *
 * `windowTo` is the paging cursor, not a filter the reader set — see {@link AuditWindow}.
 */
export function accessQueryFilters(
  filters: AuditFilters,
  windowTo?: string,
): {
  from?: string
  to?: string
  actorName?: string
  actorRole?: string
  actorOrganizationReference?: string
  source?: AuditAccessSource
  result?: AuditAccessResult
  accessType?: AuditAccessType
  downloadsOnly?: boolean
  section?: string
} {
  return {
    from: auditRangeStart(filters.dateRange),
    to: windowTo,
    actorName: filters.user === 'all' ? undefined : filters.user,
    actorRole: filters.userRole === 'all' ? undefined : filters.userRole,
    // Already an `Organization/<id>` — the drawer's option values are references.
    actorOrganizationReference: filters.clinic === 'all' ? undefined : filters.clinic,
    // 'manual' is a CHANGE source with no access-side counterpart, so it narrows nothing
    // here — the cast below would otherwise send a value the endpoint's enum rejects.
    source: isAccessSource(filters.source) ? filters.source : undefined,
    result: filters.eventResult === 'all' ? undefined : (filters.eventResult as AuditAccessResult),
    accessType: filters.emergencyOnly ? 'emergency' : undefined,
    downloadsOnly: filters.downloadsOnly ? true : undefined,
    section: filters.section === 'all' ? undefined : filters.section,
  }
}

export function applyAccessFilters(
  events: AuditAccessEvent[],
  filters: AuditFilters,
): AuditAccessEvent[] {
  const search = filters.search.trim().toLowerCase()
  return events.filter((event) => {
    if (!withinRange(event.recordedAt, filters.dateRange)) return false
    if (filters.userRole !== 'all' && event.actorRole !== filters.userRole) return false
    if (filters.clinic !== 'all' && event.actorOrganizationReference !== filters.clinic) {
      return false
    }
    if (filters.eventResult !== 'all' && event.result !== filters.eventResult) return false
    // Mirrors the pushdown in accessQueryFilters, and must: a value the ACCESS endpoint's
    // enum does not accept narrows nothing there, so it may narrow nothing here either.
    // 'manual' is the one such value a reader can pick — it names how a CHANGE was
    // authored — and a bare `!== filters.source` compared it against every access row's
    // source, matched none, and emptied Access History outright.
    if (isAccessSource(filters.source) && event.source !== filters.source) return false
    if (filters.emergencyOnly && event.accessType !== 'emergency') return false
    if (filters.downloadsOnly && !event.download) return false
    if (filters.user !== 'all' && event.actorName !== filters.user) return false
    if (filters.section !== 'all' && !event.sections.includes(filters.section)) return false
    if (search && !accessRowSearchText(event).toLowerCase().includes(search)) return false
    return true
  })
}

/**
 * Every string an access row puts on screen — the same rule as {@link
 * changeRowSearchText}, for the same reason. The row shows the actor, their role, the
 * activity and the sections touched (AccessHistoryTable.tsx); the predicate matched the
 * actor and the activity, so searching a role or a section name the reader is looking at
 * returned an empty table.
 */
export function accessRowSearchText(event: AuditAccessEvent): string {
  return [
    event.actorName,
    event.actorRole ?? '',
    event.activity,
    event.sections.map(sectionSlugLabel).join(' '),
  ].join(' ')
}

/**
 * The change-source vocabulary is coarser than the access-source one, so the inline
 * source filter maps onto it where the two overlap: the AI service authors 'ai' changes,
 * every external network authors 'integration' changes, and the remaining access sources
 * ('behuman-web', 'api', 'medplum-audit') say nothing about how a CHANGE was authored —
 * they impose no change filter, exactly as before pushdown.
 */
export function changeSourceForAccessSource(source: string): AuditChangeSource | undefined {
  if (source === 'ai-service') return 'ai'
  if (source === 'manual') return 'manual'
  if (INTEGRATION_ACCESS_SOURCES.has(source)) return 'integration'
  return undefined
}

/**
 * Whether a source-filter value is one the ACCESS endpoint's enum accepts. Keyed off
 * {@link ACCESS_SOURCE_LABELS}, whose type is `Record<AuditAccessSource, string>`, so the
 * guard cannot drift from the contract: a new access source is a compile error in that
 * map before it is a gap here.
 */
function isAccessSource(source: string): source is AuditAccessSource {
  return source !== 'all' && Object.hasOwn(ACCESS_SOURCE_LABELS, source)
}

/**
 * The change-history filters the SERVER can apply, as query input — the Greptile P1 fix
 * on !1336. These used to run client-side (applyChangeFilters) over a derivation the
 * server bounded per resource type, so a chart that hit a bound could silently omit
 * matching events; now the server materializes the complete derived set and applies
 * these as SQL predicates. `search` stays client-side on purpose: it refines what is
 * already on screen and has no server-side counterpart.
 */
export function changeQueryFilters(
  filters: AuditFilters,
  windowTo?: string,
): {
  section?: string
  from?: string
  to?: string
  actorName?: string
  actorRole?: string
  source?: AuditChangeSource
} {
  return {
    // Lazy per-section loading (S9): the one filter that also narrows the server's
    // derivation walk.
    section: filters.section === 'all' ? undefined : filters.section,
    from: auditRangeStart(filters.dateRange),
    to: windowTo,
    actorName: filters.user === 'all' ? undefined : filters.user,
    actorRole: filters.userRole === 'all' ? undefined : filters.userRole,
    source: changeSourceForAccessSource(filters.source),
  }
}

/**
 * The record + field sentence under a change row's headline. ONE definition, because two
 * screens render it (the list row and the summary card's "last modified" line) and a
 * reader who sees two spellings of one event has to work out whether they are two events.
 */
export function changeRowSubline(event: AuditChangeEvent): string {
  return `${changeRecordTitle(event)} · ${changeFieldLabel(event.field)}`
}

/**
 * Every string a change row puts on screen, which is exactly what free-text search must
 * match.
 *
 * The predicate used to match the headline and the raw actor name. Both are on the row —
 * but so are the record name, the field label and the actor's role, and searching for a
 * document name the reader can SEE returned nothing. A search box that ignores visible
 * text reads as "no results" rather than "not searched", which on a compliance surface is
 * the difference between an absent record and an unasked question.
 *
 * Deriving the haystack from the same helpers the row renders keeps that closed: a change
 * to what a row shows changes what search matches, with no second place to update.
 */
export function changeRowSearchText(event: AuditChangeEvent): string {
  return [changeEventTitle(event), changeRowSubline(event), changeActorLabel(event)].join(' ')
}

/**
 * Client-side FREE-TEXT SEARCH only. Every other change filter (date/user/role/source/
 * section) is pushed down via {@link changeQueryFilters} and applied in SQL over the
 * complete materialized set — filtering here over a bounded response was the Greptile P1.
 * Search stays client-side deliberately: it is UX-level refinement of a now-complete
 * result set (it matches the RENDERED row, which only exists client-side), not a
 * completeness-bearing predicate.
 */
export function applyChangeFilters(
  events: AuditChangeEvent[],
  filters: AuditFilters,
): AuditChangeEvent[] {
  const search = filters.search.trim().toLowerCase()
  if (!search) return events
  return events.filter((event) => changeRowSearchText(event).toLowerCase().includes(search))
}

// ---------------------------------------------------------------------------
// Export selection (S11) — ONE decision about what an export contains
// ---------------------------------------------------------------------------

export type AuditTab = 'access' | 'changes'

/**
 * What a response says about its own completeness — the two INDEPENDENT reasons a list can
 * be short, which the old single `truncated` flag conflated.
 *
 * `syncPending` is temporary and clears itself: the change derivation is still walking
 * older FHIR history, so the list will fill in as it converges. Change events only —
 * nothing is materialized for access events, so their responses never carry it.
 *
 * `capped` is permanent for this window: the page cap cut the response, and the events
 * beyond it are reachable only by moving the window (see nextWindowTo) or narrowing the
 * filters. It does NOT clear by waiting, which is exactly why one flag could not carry
 * both — a poll keyed on the conflated flag never stopped polling.
 */
export type AuditCompleteness = {
  syncPending: boolean
  capped: boolean
}

/**
 * One tab's position in the record: the exclusive upper bound currently in force, and
 * where the next window back would start.
 *
 * PAGING IS BY DATE WINDOW, not by offset. `nextWindowTo` is the server's answer to "what
 * `to` would continue from here", and it is only ever the timestamp of a COMPLETE group:
 * when the cap cuts inside a run of events sharing one `recordedAt`, the store drops that
 * whole partial run before reporting. That is what makes an exclusive `to` lossless in both
 * directions — no event falls between two consecutive windows, and none is returned twice.
 *
 * A MISSING `nextWindowTo` ON A CAPPED PAGE IS NOT THE END OF THE RECORD. It means the
 * whole page is one timestamp group, so the window has nowhere to advance to: the events
 * beyond it are reachable only by narrowing the filters. Reading absence as "that is
 * everything" would turn the one case where the reader must act into the one case where
 * they are told nothing is wrong.
 */
export type AuditWindow = {
  /** The `to` currently sent, or undefined for the most recent window. */
  to?: string
  /** The `to` that would open the next window back, when the server offered one. */
  nextTo?: string
}

/**
 * The rows an export carries, plus what the reader needs to know about them.
 *
 * A discriminated union rather than two arrays: the two tabs have different columns, and
 * the tab is what the header, the filename and the column set all key off.
 */
export type AuditExportSelection =
  | ({ tab: 'access'; events: AuditAccessEvent[] } & AuditCompleteness)
  | ({ tab: 'changes'; events: AuditChangeEvent[] } & AuditCompleteness)

/**
 * Picks the rows the CSV and PDF exports both carry.
 *
 * Both formats call this and nothing else, so they cannot disagree about what "the
 * filtered set" means — for a compliance artefact that divergence would be a correctness
 * bug, not untidiness. It takes the arrays the tables RENDER rather than re-running the
 * predicates, so an export can also never disagree with the screen it was taken from.
 *
 * Both tabs carry the server's `capped`; only the change tab can carry `syncPending`, and
 * access is passed `false` for it rather than the field being optional, so neither export
 * format has to know which tab it is holding to decide what to say.
 */
export function selectAuditExportRows(input: {
  tab: AuditTab
  filteredAccess: AuditAccessEvent[]
  filteredChanges: AuditChangeEvent[]
  access: AuditCompleteness
  changes: AuditCompleteness
}): AuditExportSelection {
  return input.tab === 'access'
    ? { tab: 'access', events: input.filteredAccess, ...input.access }
    : { tab: 'changes', events: input.filteredChanges, ...input.changes }
}

export const AUDIT_TAB_LABELS: Record<AuditTab, string> = {
  access: 'Access History',
  changes: 'Change History',
}

/**
 * What each tab's rows actually guarantee — stated once, because three surfaces say it:
 * the tab header, the Change History detail panel, and the PDF's subtitle.
 *
 * The two tabs are backed by different things and only one of them is immutable. Migration
 * 0010 makes `audit_access_event` append-only IN THE DATABASE: UPDATE, DELETE, TRUNCATE and
 * TRIGGER are revoked from the application role and an ALWAYS trigger raises on any of them.
 * Migration 0014 deliberately does NOT do that for `audit_change_event` — no REVOKE, no
 * trigger — because that table is a rebuildable materialization of FHIR version history,
 * wiped and re-derived on demand, with FHIR history remaining the record.
 *
 * So the blanket "Audit records are immutable" was false on the Change History tab, and
 * falsely reassuring in exactly the place a compliance reader would rely on it. What IS
 * true there is the provenance: the row is reconstructed from a version chain that is never
 * rewritten.
 */
export const AUDIT_INTEGRITY_STATEMENTS: Record<AuditTab, string> = {
  access: 'Access records are immutable',
  changes: 'Derived from FHIR version history',
}

/**
 * The columns and cell values an export carries, shared by both formats for the same
 * reason the row selection is: a CSV and a PDF of the same tab that render a row
 * differently are two different records of one event.
 *
 * Access columns mirror the Access History table's visible columns; change columns mirror
 * the Change History rows plus their detail-panel fields.
 */
export const ACCESS_EXPORT_COLUMNS = ['User', 'Date & time', 'Activity', 'Result', 'Source']

export function accessExportRow(event: AuditAccessEvent): string[] {
  return [
    event.actorRole ? `${event.actorName} (${event.actorRole})` : event.actorName,
    `${formatAuditDate(event.recordedAt)} ${formatAuditTime(event.recordedAt)}`,
    // Section NAMES, as the table renders them — not the raw dataKey slugs.
    event.sections.length > 0
      ? `${event.activity} — ${event.sections.map(sectionSlugLabel).join('; ')}`
      : event.activity,
    // A failed or denied access exported as though it succeeded is the one error this
    // artefact cannot make: the export is the copy a compliance reader keeps, and the
    // list's own row shows the result only when expanded. The screen's word, not the
    // stored enum.
    RESULT_LABELS[event.result],
    ACCESS_SOURCE_LABELS[event.source],
  ]
}

export const CHANGE_EXPORT_COLUMNS = [
  'Change',
  'Date & time',
  'Changed by',
  'Source',
  'Field',
  'Before',
  'After',
]

export function changeExportRow(event: AuditChangeEvent): string[] {
  return [
    // Named, exactly as the row on screen is (changeRecordTitle). An export that says
    // "Added insurance card" three times where the screen distinguishes them is the
    // "two different records of one event" this file's export rule forbids — and the
    // export is the copy a compliance reader keeps.
    changeEventTitle(event, changeRecordTitle(event)),
    `${formatAuditDate(event.recordedAt)} ${formatAuditTime(event.recordedAt)}`,
    changeActorLabel(event),
    CHANGE_SOURCE_LABELS[event.source],
    // The reader-facing label, as the row and the detail pane both render it. The raw
    // FHIR path shipped here while the screen said "status tags", so the export and the
    // screen described one change in two vocabularies — and `changeFieldLabel` exists
    // precisely to keep `meta.tag` away from a reader.
    changeFieldLabel(event.field),
    event.before ?? '',
    event.after ?? '',
  ]
}

/** The header row and cell values for whichever tab the selection carries. */
export function auditExportTable(selection: AuditExportSelection): {
  columns: string[]
  rows: string[][]
} {
  return selection.tab === 'access'
    ? { columns: ACCESS_EXPORT_COLUMNS, rows: selection.events.map(accessExportRow) }
    : { columns: CHANGE_EXPORT_COLUMNS, rows: selection.events.map(changeExportRow) }
}

export function buildAuditExportFilename(
  tab: AuditTab,
  patientId: string,
  extension: 'csv' | 'pdf',
): string {
  const date = new Date().toISOString().slice(0, 10)
  const slug = tab === 'access' ? 'access-history' : 'change-history'
  return `chart-audit-${slug}-${patientId}-${date}.${extension}`
}

const optionLabel = (
  options: Array<{ value: string; label: string }>,
  value: string,
  fallback: string,
): string => options.find((option) => option.value === value)?.label ?? fallback

/**
 * Four of the drawer's dimensions describe an ACCESS event and nothing else: a result, an
 * organization, an access type, a download marker. A change event carries none of them and
 * `changeQueryFilters` sends none of them, so on the Change History tab they narrow
 * nothing at all — the selects simply sit there holding a value.
 *
 * They are still LISTED on that tab, marked, rather than dropped: the reader of a detached
 * document has to be able to tell "this dimension did not apply" apart from "this
 * dimension is missing from the export".
 */
const NOT_APPLICABLE_TO_CHANGES = 'Not applicable to Change History'

/**
 * What the Source select actually did to the tab being exported.
 *
 * One select, two vocabularies. An access source reaches the change query only through
 * {@link changeSourceForAccessSource}, and 'manual' — the one pure change source — reaches
 * the access query not at all. So the same picked value can be a real predicate on one tab
 * and inert on the other, and the document must not present the second case as the first.
 */
function describeSourceFilter(source: string, tab: AuditTab): string {
  const label = optionLabel(SOURCE_OPTIONS, source, source)
  if (source === 'all') return label
  const narrowed =
    tab === 'access' ? isAccessSource(source) : changeSourceForAccessSource(source) !== undefined
  return narrowed ? label : `${label} — did not narrow ${AUDIT_TAB_LABELS[tab]}`
}

/**
 * Every filter dimension and its current value FOR THE TAB BEING EXPORTED, as
 * reader-facing label/value pairs.
 *
 * Every dimension is listed, not only the narrowed ones: a compliance reader holding the
 * file months later has to be able to see that "All users" was in force rather than infer
 * it from an absent line. The labels come from the same option lists the drawer and the
 * inline bar render, so the document names a filter exactly as the operator saw it.
 *
 * The tab is required, not optional. This block is the export's claim about how its rows
 * were selected, and half these dimensions select nothing on the Change History tab — a
 * document that printed "Emergency access only: Yes" above a change table was stating a
 * filter that never ran against a single row in it.
 */
export function describeAuditFilters(
  filters: AuditFilters,
  clinicOptions: Array<{ value: string; label: string }>,
  roleOptions: Array<{ value: string; label: string }>,
  tab: AuditTab,
): Array<[string, string]> {
  const accessOnly = (value: string) => (tab === 'access' ? value : NOT_APPLICABLE_TO_CHANGES)
  return [
    ['Date range', optionLabel(DATE_RANGE_OPTIONS, filters.dateRange, filters.dateRange)],
    ['User', filters.user === 'all' ? 'All users' : filters.user],
    ['User role', optionLabel(roleOptions, filters.userRole, filters.userRole)],
    ['Section', filters.section === 'all' ? 'All sections' : sectionSlugLabel(filters.section)],
    [
      'Event result',
      accessOnly(optionLabel(EVENT_RESULT_OPTIONS, filters.eventResult, filters.eventResult)),
    ],
    // The reference is the value the server matched on; the name is what the operator
    // picked. Show the name, falling back to the reference when the option list no longer
    // carries it.
    [
      'Clinic / organization',
      accessOnly(optionLabel(clinicOptions, filters.clinic, filters.clinic)),
    ],
    ['Source', describeSourceFilter(filters.source, tab)],
    ['Downloads and exports only', accessOnly(filters.downloadsOnly ? 'Yes' : 'No')],
    ['Search', filters.search.trim() ? `"${filters.search.trim()}"` : 'None'],
  ]
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatAuditDate(iso: string): string {
  return dayjs(iso).format('MMM D, YYYY')
}

export function formatAuditTime(iso: string): string {
  return dayjs(iso).format('h:mm A')
}

export function formatAuditDayTime(iso: string): string {
  const day = dayjs(iso)
  const prefix = day.isSame(dayjs(), 'day') ? 'Today' : day.format('MMM D, YYYY')
  return `${prefix}, ${day.format('h:mm A')}`
}
